import { Action, StoreType } from 'reducers/types';
import {
  PROGRAMS_ACTION_TYPES,
  PROGRAM_SCHEDULE_ACTION_TYPES,
  PROGRAM_TRACK_ACTION_TYPES,
  RESTART_PROGRAM_ACTION_TYPES,
  SCHEDULE_ACTION_TYPES,
  ProgramScheduleActionPayload,
} from 'actions/programs';
import { ProgramsSource, ProgramsState, Program } from './types';

const initialState: ProgramsState = {
  total: 0,
  all: [],
  error: null,
  loading: false,
};

interface ProgramPayload extends ProgramsSource {
  error?: string;
}

const withSortedSessions = (programTrack: Program['programTrack']) =>
  programTrack
    ? {
        ...programTrack,
        programTrackSessions: programTrack.programTrackSessions
          ? [...programTrack.programTrackSessions].sort((a, b) => a.order - b.order)
          : [],
      }
    : programTrack;

const extractPrograms = (payload: ProgramPayload): Program[] => {
  const { userPrograms } = payload;

  return userPrograms.map(
    ({
      program,
      schedule,
      programTrack,
      completedSessionsCount,
      lastSession = null,
      nextSession = null,
      nextTrackSessionId = null,
    }): Program => ({
      ...program,
      id: program.id,
      schedule,
      programTrack: withSortedSessions(programTrack),
      lastSession,
      nextSession,
      nextTrackSessionId,
      completedSessionsCount,
    }),
  );
};

const mergeProgramsResetSession = (state: ProgramsState, payload: ProgramPayload) =>
  state.all.map((item) => {
    const updatedProgram = payload.userPrograms.find((i) => i.program.id === item.id);
    if (!updatedProgram) {
      return item;
    }

    return {
      ...item,
      programTrack: withSortedSessions(updatedProgram.programTrack) || item.programTrack,
      nextSession: updatedProgram.nextSession ?? null,
      nextTrackSessionId: updatedProgram.nextTrackSessionId ?? null,
    };
  });

const mergePrograms = (state: ProgramsState, payload: ProgramPayload) =>
  state.all.map((item) => {
    const updatedProgram = payload.userPrograms.find((i) => i.program.id === item.id);
    if (!updatedProgram) {
      return item;
    }

    return {
      ...item,
      ...updatedProgram.program,
      schedule: updatedProgram.schedule,
      programTrack: withSortedSessions(updatedProgram.programTrack) || item.programTrack,
    };
  });

const applySchedule = (
  state: ProgramsState,
  { programId, schedule }: ProgramScheduleActionPayload,
) => state.all.map((item) => (item.id === programId ? { ...item, schedule } : item));

const isScheduleUpdate = (payload: unknown): payload is ProgramScheduleActionPayload =>
  typeof (payload as ProgramScheduleActionPayload)?.programId === 'number';

export default function Index(state = initialState, action: Action<ProgramPayload>) {
  switch (action.type) {
    case PROGRAMS_ACTION_TYPES.LOADING:
      if (state.loading || state.all.length > 0) {
        return state;
      }

      return {
        ...initialState,
        loading: true,
      };

    case PROGRAMS_ACTION_TYPES.LOADED:
      return {
        ...state,
        total: action.payload.total,
        all: extractPrograms(action.payload),
        loading: false,
        error: null,
      };

    case SCHEDULE_ACTION_TYPES.UPDATED:
      return {
        ...state,
        all: mergePrograms(state, action.payload),
      };

    case PROGRAM_SCHEDULE_ACTION_TYPES.UPDATING:
      return isScheduleUpdate(action.payload)
        ? { ...state, all: applySchedule(state, action.payload) }
        : state;

    case PROGRAM_SCHEDULE_ACTION_TYPES.UPDATED:
      return {
        ...state,
        all: mergePrograms(state, action.payload),
      };

    case PROGRAM_TRACK_ACTION_TYPES.CHANGED:
      return {
        ...state,
        all: mergePrograms(state, action.payload),
      };

    case RESTART_PROGRAM_ACTION_TYPES.RESTARTED:
      return {
        ...state,
        all: mergeProgramsResetSession(state, action.payload),
      };

    default:
      return {
        ...state,
      };
  }
}

export const getPrograms = (state: StoreType): ProgramsState => state.programs;

export const getProgramById = (state: StoreType, id: number): Program | undefined => {
  const { all: programs } = getPrograms(state);
  return programs.find((program) => program.id === id);
};
